import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputNote,
  InputText,
} from "@sellar-io/components";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Icon, SpinnerLegacy } from "../../../../components";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import { useSupplier } from "../../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierUpdate } from "../../../../hooks/api/v2/supplier/useSupplierUpdate";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";

export type SupplierInvoiceCreationSettingsSettingsFieldValues = {
  invoiceCCEmailList: string;
  registeredCompanyName: string | null;
};

export type InvoiceCreationStage = "confirmed" | "dispatched" | "delivered";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmails = (value: string): true | string => {
  if (!value.trim()) {
    return true;
  }

  const emails = value.split(",").map((email) => email.trim());
  const allValid = emails.every((email) => emailRegex.test(email));

  return allValid || "Please enter valid email(s), separated by commas";
};

export const BusinessSupplierInvoiceCreationStageSettingsCard = ({
  supplierId,
}: {
  supplierId: number;
}) => {
  const [dialogActive, setDialogActive] = useState<boolean>(false);
  const [invoiceCreationStage, setInvoiceCreationStage] =
    useState<InvoiceCreationStage>();
  const supplierQuery = useSupplier(supplierId);
  const updateSupplierMutation = useSupplierUpdate(supplierId);
  const form = useForm<SupplierInvoiceCreationSettingsSettingsFieldValues>({
    shouldUseNativeValidation: true,
  });
  const cardWithDepositEnabled =
    useRemoteFeatureFlags().isActive("cardWithDeposit");

  const openDialog = () => {
    if (!supplierQuery.data) return;

    form.reset({
      invoiceCCEmailList:
        supplierQuery.data.invoiceCCEmailList?.join(", ") ?? "",
      registeredCompanyName: supplierQuery.data.Business.registeredCompanyName,
    });
    setInvoiceCreationStage(supplierQuery.data.invoiceCreationStage);
    setDialogActive(true);
  };

  const displayedInvoiceCreationStage =
    invoiceCreationStage ?? supplierQuery.data?.invoiceCreationStage;
  const invoiceCreationStageText =
    displayedInvoiceCreationStage === "confirmed"
      ? "finalised"
      : displayedInvoiceCreationStage;

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge("bg-white", "flex items-center justify-between p-4")}
      >
        <div className="flex flex-col gap-2">
          <H5>Invoice settings</H5>
          <div className="text-sm text-gray-500">
            Configure customer invoices and how dates are defined
          </div>
        </div>
        <Button onClick={openDialog} disabled={!supplierQuery.data}>
          <Icon size={0.7} icon={"PENCIL"} />
          Edit
        </Button>
      </CardHeader>
      <CardContent className={"flex flex-col gap-4 p-4"}>
        <div className="flex flex-col">
          <H5>Your registered company name to display on invoices</H5>
          <p>
            {supplierQuery.data?.Business.registeredCompanyName ?? "Not set"}
          </p>
        </div>
        <div className="flex flex-col">
          <H5>Calculate issue date and payment due date from</H5>

          <p>
            {cardWithDepositEnabled
              ? "Issued on buyer charged remaining order balance"
              : `Order ${invoiceCreationStageText}`}
          </p>
        </div>
        <div className="flex flex-col">
          <H5>Email addresses to CC when issuing a customer's invoice</H5>
          <p>
            {supplierQuery.data?.invoiceCCEmailList?.join(", ") || "Not set"}
          </p>
        </div>
      </CardContent>
      <Dialog active={dialogActive} onDismiss={() => setDialogActive(false)}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setDialogActive(false)}
          title={`Invoice settings`}
          description={"Configure customer invoices and how dates are defined"}
        />
        <DialogContent>
          {updateSupplierMutation.isPending ? (
            <SpinnerLegacy />
          ) : (
            <>
              <form
                onSubmit={form.handleSubmit((data) => {
                  const sanitizedInvoiceCCEmailList = data.invoiceCCEmailList
                    .split(",")
                    .map((email) => email.trim().toLowerCase())
                    .filter((email) => email !== "");

                  updateSupplierMutation.mutate(
                    {
                      invoiceCreationStage,
                      invoiceCCEmailList: sanitizedInvoiceCCEmailList,
                      Business: {
                        registeredCompanyName:
                          data.registeredCompanyName &&
                          data.registeredCompanyName !== ""
                            ? data.registeredCompanyName
                            : null,
                      },
                    },
                    {
                      onSuccess: () => setDialogActive(false),
                    },
                  );
                })}
              >
                <InputLabel>
                  Your registered company name to display on customer invoices
                </InputLabel>
                <InputContainer>
                  <InputText {...form.register("registeredCompanyName")} />
                </InputContainer>
                <InputNote className={"mb-2"}>
                  Set your registered company name if it differs from your
                  trading name. If this field is left blank, we will display
                  your trading name on invoices issued to your customers.
                </InputNote>
                {/* Invoice creation stage cannot be set for cardWithDeposit payment type.
        The invoice is automatically created on charge buyer remaining order balance. */}
                {!cardWithDepositEnabled && (
                  <>
                    <RadioGroup
                      value={displayedInvoiceCreationStage}
                      onChange={(e) => {
                        setInvoiceCreationStage(e as InvoiceCreationStage);
                      }}
                    >
                      <H5 className={"mb-2"}>
                        Calculate issue date and payment due date from
                      </H5>
                      <RadioGroupContainer>
                        <RadioGroupOption value={"confirmed"}>
                          Order finalised
                        </RadioGroupOption>
                        <RadioGroupOption value={"dispatched"}>
                          Order dispatched
                        </RadioGroupOption>
                        <RadioGroupOption value={"delivered"}>
                          Order delivered
                        </RadioGroupOption>
                      </RadioGroupContainer>
                    </RadioGroup>

                    <H6 className={"mt-2"}>
                      Invoices won't be issued to your customers until you mark
                      their order as {invoiceCreationStageText}. Their payment
                      due date will be calculated forward from that date as per
                      their credit terms.
                    </H6>
                  </>
                )}
                <div className="mt-4">
                  <InputLabel>
                    Email addresses to CC when issuing a customer's invoice
                  </InputLabel>
                  <InputContainer>
                    <InputText
                      {...form.register("invoiceCCEmailList", {
                        required: false,
                        validate: {
                          validate: validateEmails,
                        },
                      })}
                      placeholder="email1@domain.com, email2@domain.com"
                    />
                  </InputContainer>
                  <InputNote>
                    Get copied in to all invoice emails sent to your customers
                    by adding one or more of your own email addresses. Enter
                    each email address separated by a comma (Example:
                    email1@business.com, email2@business.com,
                    email3@business.com)
                  </InputNote>
                </div>
                <DialogCTA>
                  <Button
                    loading={updateSupplierMutation.isPending}
                    type={"submit"}
                  >
                    Confirm
                  </Button>
                </DialogCTA>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
